import React, { useEffect, useState } from "react";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
} from "components/ui";
import {
  AddHobbyAction,
  UpdateHobbyAction,
} from "store/middlewares/dataManagement/hobby";
import { openToast } from "store/slices/common/toast.slice";
import DefaultImg from "assets/images/PNG/defaultImage.jpg";
import ImageEdit from "assets/images/SVG/ImageEditIcon.svg";
import { SubmitLoaderButton } from "components/atoms";

function ModalEditHobby({ row }) {
  const dispatch = useDispatch();
  const [formError, setFormError] = useState(false);
  const { settings } = useSelector((state) => state.settings);

  const handleSubmit = (values) => {
    if (row) {
      dispatch(
        UpdateHobbyAction({
          id: row.id,
          ...values,
          onSuccess: () => formik.setSubmitting(false),
          onError: () => setFormError(true),
        }),
      );
    } else {
      dispatch(
        AddHobbyAction({
          ...values,
          filter: { page: 1, pageSize: 10 },
          onSuccess: () => formik.setSubmitting(false),
          onError: () => setFormError(true),
        }),
      );
    }
  };

  const formik = useFormik({
    initialValues: {
      name: row?.name ? row?.name : "",
      file: null,
      image: row?.image ?? "",
      arName: row?.ar_name ? row?.ar_name : "",
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Hobby name is required"),
      image: Yup.string().required("Image is required"),
      arName: Yup.string().required("Hobby name is required"),
    }),
    enableReinitialize: true,
    onSubmit: handleSubmit,
  });

  const [previewImage, setPreviewImage] = useState();

  useEffect(() => {
    if (row) {
      setPreviewImage(row?.imageUrl);
    }
  }, [row]);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    let fileSizeLimit = settings?.upload_image_size;
    const reader = new FileReader();

    if (file) {
      if (!file.type.match(/^image\/(png|jpeg|jpg|svg(\+xml)?)$/)) {
        dispatch(
          openToast({
            type: "error",
            message: "Only images (png, jpeg, jpg, svg) are allowed.",
          }),
        );
        return;
      }

      if (file.size > fileSizeLimit * 1024 * 1024) {
        dispatch(
          openToast({
            type: "error",
            message: `Please upload images less than ${fileSizeLimit}MB.`,
          }),
        );
        return;
      }

      reader.onload = () => {
        formik.setFieldValue("image", reader.result);
        formik.setFieldValue("file", file);
        formik.setFieldError("image", "");
        formik.setFieldTouched("image", "");
      };

      reader.readAsDataURL(file);
      setPreviewImage(URL.createObjectURL(file));
    }
  };

  return (
    <form onSubmit={formik.handleSubmit}>
      <div className="grid gap-5 lg:gap-7.5 mx-auto">
        <Card className="min-w-full">
          <CardHeader>
            <CardTitle>Hobby</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-5">
            <div className="flex flex-col mt-2 justify-center items-center gap-x-2.5">
              <div className="relative">
                <div className="size-24 overflow-hidden rounded-[50%]">
                  <img
                    src={previewImage ?? DefaultImg}
                    alt="flag"
                    className="w-full h-full object-cover"
                  />
                </div>

                <div className="absolute bottom-0 right-0 h-[35px] w-[35px] rounded-[50%] bg-white flex items-center justify-center">
                  <input
                    type="file"
                    name="file"
                    accept="image/png, image/jpeg,image/jpg"
                    id="editimg"
                    onChange={(e) => {
                      handleFileChange(e);
                    }}
                    hidden
                  />
                  <label htmlFor="editimg" className="cursor-pointer">
                    <img src={ImageEdit} alt={""} />
                  </label>
                </div>
              </div>
              {formik.errors.image && formik.touched.image && (
                <div className="text-[0.8125rem] text-red-500 text-center">
                  {formik.errors.image}
                </div>
              )}
            </div>
            <div className="flex items-baseline flex-wrap lg:flex-nowrap gap-2.5">
              <Label className="flex w-full max-w-56">Name (English)</Label>
              <Input
                error={formik.errors.name && formik.touched.name}
                errorMsg={formik.errors.name}
                value={formik.values.name}
                type="text"
                name="name"
                maxLength={50}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                isTextOnly={true}
                placeholder="Enter hobby"
                className={"!py-0 w-full"}
              />
            </div>
            <div className="flex items-baseline flex-wrap lg:flex-nowrap gap-2.5">
              <Label className="flex w-full max-w-56">Name (Arabic)</Label>
              <Input
                error={formik.errors.arName && formik.touched.arName}
                errorMsg={formik.errors.arName}
                value={formik.values.arName}
                type="text"
                name="arName"
                maxLength={50}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                isTextOnly={true}
                placeholder="Enter hobby"
                className={"!py-0 w-full"}
              />
            </div>
            <div className="flex justify-center">
              <SubmitLoaderButton
                btnText={row ? "Update" : "Add"}
                isDisable={formik.isSubmitting && !formError}
              />
            </div>
          </CardContent>
        </Card>
      </div>
    </form>
  );
}

export default ModalEditHobby;
